import api from '../api/offlineClient';
import { preCacheEndesa } from '../utils/endesaFileCache';

const RATE_KEY = 'sgv_cache_ts';
const MIN_INTERVAL = 2 * 3600 * 1000;

function currentMes() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

function currentYear() {
  return new Date().getFullYear();
}

const PREFETCH = [
  // Módulos principales
  '/api/endesa/docs',
  '/api/installations/list',
  '/api/actas/instalaciones',
  '/api/actas/instalaciones?search=&page=1&limit=20',
  // Documentos propios
  '/api/documents/mine',
  '/api/documents/all',
  '/api/documents/mine/stats',
  // Calendario
  '/api/calendar/vacaciones',
  `/api/calendar/festivos?año=${currentYear()}`,
  // Fichaje mes actual
  `/api/fichajes/mine/month?mes=${currentMes()}`,
  // Instalaciones con params que usa el componente
  '/api/installations/list?page=1&limit=25',
  // Empleados
  '/api/employees/list',
  '/api/employees/me',
  '/api/employees',
  // Selectores para partes y nota gastos
  '/api/installations/dropdown',
  '/api/inventario/lista',
  '/api/inventario/materiales',
  '/api/inventario/movimientos',
  // Perfil
  '/api/facturas/mine',
  '/api/nominas/mine',
  // Planificación (solo admins — se ignora si da 403)
  '/api/planificacion/empleados',
];

// Sincroniza los localStorage que usan los iframes (nota-gastos.html, partes.html)
// para que funcionen offline aunque nunca se hayan abierto.
async function syncIframeCaches() {
  try {
    const dropdownRes = await api.get('/api/installations/dropdown').catch(() => null);
    if (dropdownRes?.data) {
      const raw = dropdownRes.data;
      // sgv_inst_cache → formato nota-gastos.html (INST_DB)
      const instDb = raw.map(r => ({
        num: r.numero_instalacion || '',
        dir: r.direccion || '',
        n:   r.descripcion || '',
        p:   r.provincia || '',
        c:   r.cliente || '',
      }));
      localStorage.setItem('sgv_inst_cache', JSON.stringify(instDb));

      // sgv_inst_api_cache → formato partes.html (INST_API)
      const instApi = raw.map(r => ({
        num: r.numero_instalacion || '',
        dir: r.direccion || '',
        n:   r.descripcion || r.nombre || '',
        u:   r.usuario_responsable || '',
        p:   r.provincia || '',
        c:   r.cliente || '',
      })).filter(r => r.num || r.n);
      localStorage.setItem('sgv_inst_api_cache', JSON.stringify(instApi));
    }
  } catch {}

  try {
    const invRes = await api.get('/api/inventario/lista').catch(() => null);
    if (invRes?.data) {
      localStorage.setItem('sgv_inv_mat_cache', JSON.stringify(invRes.data));
    }
  } catch {}

  try {
    const [meRes, empsRes] = await Promise.all([
      api.get('/api/employees/me').catch(() => null),
      api.get('/api/employees').catch(() => null),
    ]);
    const meEmp  = meRes?.data  || null;
    const emps   = empsRes?.data || [];
    if (meEmp || emps.length) {
      localStorage.setItem('sgv_emps_cache', JSON.stringify({ emps, meEmp }));
    }
  } catch {}
}

class DataCache {
  async preFetch(force = false) {
    if (!navigator.onLine) return;

    const last = parseInt(localStorage.getItem(RATE_KEY) || '0', 10);
    if (!force && Date.now() - last < MIN_INTERVAL) return;

    // Lanzar todas las peticiones en paralelo (el interceptor las cachea en IndexedDB)
    await Promise.allSettled(PREFETCH.map(url => api.get(url).catch(() => {})));

    // Poblar caches de localStorage para iframes (partes, nota-gastos)
    syncIframeCaches().catch(() => {});

    // Pre-cachear archivos binarios de Endesa en Cache API
    api.get('/api/endesa/docs').then(docsRes => {
      const docs = docsRes?.data || [];
      if (docs.length) {
        preCacheEndesa(
          docs,
          id => api.get(`/api/endesa/${id}/signed-url`),
          id => api.get(`/api/endesa/${id}/file`, { responseType: 'blob' })
        ).catch(() => {});
      }
    }).catch(() => {});

    localStorage.setItem(RATE_KEY, String(Date.now()));
  }

  invalidate() {
    localStorage.removeItem(RATE_KEY);
  }
}

const dataCache = new DataCache();
export default dataCache;
